import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { Close, Edit, InfoOutlined, Map } from "@material-ui/icons";
import _ from "lodash";
import moment from "moment";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FaArrowLeft, FaArrowRight, FaPlus } from "react-icons/fa";
import { toast } from "react-toastify";

// Components
import Tooltip from "../../../common/Tooltip";
import AvgMovingChart from "./Chart";
import CreateIndexModel from "./CreateIndexModel";
import InsertIndexModel from "./InsertIndexModel";

// Utilities & Actions
import { FaMagnifyingGlass } from "react-icons/fa6";
import { toastError, toastSuccess } from "../../../utils";
import { portsByChar } from "../Calculator/actions";
import * as actions from "./actions";

const useStyles = makeStyles({
  root: {
    width: "fit-content",
  },
});

// --- Helper Components ---

function CustomTooltip({ children, content }) {
  const [isVisible, setIsVisible] = useState(false);
  const wrapperRef = useRef(null);
  const tooltipRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [position, setPosition] = useState("middle");

  useLayoutEffect(() => {
    if (!isVisible || !wrapperRef.current || !tooltipRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();
    const tooltipHeight = tooltipRef.current.offsetHeight;
    const windowHeight = window.innerHeight;

    let top = rect.top + rect.height / 2;
    let pos = "middle";

    if (top - tooltipHeight / 2 < 8) {
      top = rect.bottom - rect.height / 2 - 48;
      pos = "bottom";
    } else if (top + tooltipHeight / 2 > windowHeight - 8) {
      top = rect.top + rect.height / 2 + 48;
      pos = "top";
    }

    setCoords({
      top,
      left: rect.right + 12,
    });
    setPosition(pos);
  }, [isVisible, content]);

  return (
    <div
      ref={wrapperRef}
      className="relative inline-block w-full"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div
          ref={tooltipRef}
          style={{
            top: coords.top,
            left: coords.left,
            transform:
              position === "middle"
                ? "translate(0, -50%)"
                : position === "top"
                  ? "translate(0, -100%)"
                  : "translate(0, 0)",
          }}
          className={`
            fixed z-[99999] w-[520px] bg-white text-gray-900 rounded-md 
            border-2 border-[#313C4C]/20 p-3 shadow-xl
            before:content-[''] before:absolute before:border-8 before:border-transparent
            before:right-full
            ${position === "middle"
              ? "before:top-1/2 before:-translate-y-1/2 before:border-r-[#313C4C]/20"
              : position === "top"
                ? "before:bottom-2 before:border-r-[#313C4C]/20"
                : "before:top-2 before:border-r-[#313C4C]/20"
            }
          `}
        >
          <div
            className={`
              relative bg-white rounded-md
              before:content-[''] before:absolute before:border-8 before:border-transparent
              before:right-full
              ${position === "middle"
                ? "before:top-1/2 before:-translate-y-1/2 before:border-r-white"
                : position === "top"
                  ? "before:bottom-2 before:border-r-white"
                  : "before:top-2 before:border-r-white"
              }
            `}
          >
            {content}
          </div>
        </div>
      )}
    </div>
  );
}

function MyMarketIndex(props) {
  const { setLoader = () => { } } = props;
  const [isCsvUploaded, setIsCsvUploaded] = useState(false);

  // State
  const [subLoader, setSubLoader] = useState(false);
  const [hideSideBar, setHideSidebar] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Data State
  const [vesselIndexesData, setVesselIndexesData] = useState([]);
  const [vesselIndexesDetails, setVesselIndexesDetails] = useState([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);

  const [selectedPort, setSelectedPort] = useState({ label: "", value: "" });
  const [portOptions, setPortOptions] = useState([]);
  const [portLoading, setPortLoading] = useState(false);

  // Date Filter State
  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(10, "years").toISOString(),
    endDate: moment().toISOString(),
  });
  const [prevFilters, setPrevFilters] = useState({
    selectedOptions: [],
    startDate: null,
    endDate: null,
  });

  // Modal States
  const [isModelOpen, setIsmodelOpen] = useState(false); // Create
  const [indexEditModelOpen, setIndexEditModelOpen] = useState(false); // Edit
  const [isInsertIndex, setIsInsertIndex] = useState(false); // Insert Data
  const [isOpenVesselIndexInfoModel, setisOpenVesselIndexInfoModel] =
    useState(false); // Info

  // Form/Input States
  const [selectedIndex, setSelectedIndex] = useState("");
  const [indexValue, setIndexValue] = useState([{ indexValue: 0, Date: "" }]);

  // Initialize with default form data
  const initialFormState = {
    indexName: "",
    indexType: "",
    inputType: "manual",
    access: "onlyme",
    briefDescription: "",
    indexDetails: "",
    vesselDetails: "",
    port: [{}],
  };

  const [formData, setFormData] = useState(initialFormState);
  const [vesselIndexInfo, setVesselIndexInfo] = useState(initialFormState);

  // Refs
  const selectedOptionsRef = useRef(selectedOptions);
  const shouldFetchRef = useRef(false);

  const isSuperAdmin =
    JSON.parse(localStorage.getItem("auth"))?.role === "superadmin";

  // --- Effects ---

  useEffect(() => {
    selectedOptionsRef.current = selectedOptions;
  }, [selectedOptions]);

  // Initial Fetch
  useEffect(() => {
    fetchVesselIndexes();
    fetchVesselIndexList();
  }, []);

  // Set default selection based on ownership (or first available)
  useEffect(() => {
    if (vesselIndexesDetails.length > 0 && selectedOptions.length === 0) {
      // Prefer owned indexes, otherwise just take the first few
      const ownerIndexes = vesselIndexesDetails
        .filter((val) => val.isOwner)
        .slice(0, 4);
      const defaultSelection =
        ownerIndexes.length > 0
          ? ownerIndexes
          : vesselIndexesDetails.slice(0, 4);

      setSelectedOptions(defaultSelection);
      selectedOptionsRef.current = defaultSelection;
      fetchVesselIndexes();
    }
  }, [vesselIndexesDetails]);

  // Trigger fetch when flags set
  useEffect(() => {
    if (shouldFetchRef.current) {
      fetchVesselIndexes();
    }
  }, [shouldFetchRef.current]);

  // --- Data Fetching ---

  const fetchVesselIndexList = useCallback(async () => {
    try {
      setLoader(true);
      const res = await actions.getMyIndexList();
      setLoader(false);
      setVesselIndexesDetails(res?.data ?? []);
    } catch (error) {
      setLoader(false);
      toast.error("Failed to fetch vessel indexes list");
      console.error(error);
    }
  }, [setLoader]);

  const fetchVesselIndexes = useCallback(
    async (force = false) => {
      if (!selectedOptionsRef.current.length) return;

      const filter = {
        selectedOptions: selectedOptionsRef.current.map((o) => o.indexName),
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      };

      if (!force && JSON.stringify(prevFilters) === JSON.stringify(filter))
        return;

      try {
        setLoader(true);
        shouldFetchRef.current = false;

        const res = await actions.getVesselIndexes(filter);

        setVesselIndexesData(res?.data ?? []);
        setPrevFilters(filter);
      } catch (error) {
        toast.error("Failed to fetch vessel indexes");
        console.error(error);
      } finally {
        setLoader(false);
        setHasFetched(true);
      }
    },
    [dateRange, prevFilters],
  );

  // --- Logic & Handlers ---

  const onChangePort = (name, value) => {
    setSelectedPort({
      port: value?.name,
      selectedPort: value,
      lat: value?.lat || "",
      lng: value?.lng || "",
      label: value?.name,
      value: value?.value,
    });
  };
  const delayPortSearch = useCallback(
    _.debounce((search) => onSearchPortList(search), 500),
    [],
  );

  const onSearchPortList = (value) => {
    if (value?.length > 2) {
      setPortLoading(true);
      portsByChar(value)
        .then((res) => {
          setPortLoading(false);
          setPortOptions(
            Array.isArray(res.data.list)
              ? res.data.list.map((item) => ({
                ...item,
                portLabel: `${item.name}${item.country ? ` (${item.country})` : ""
                  }`,
              }))
              : [],
          );
        })
        .catch((err) => {
          setPortLoading(false);
        });
    }
  };

  const toggleVesselIndexSelection = useCallback(({ indexName, _id }) => {
    setSelectedOptions((prevSelected) => {
      let newSelection;
      const isSelected = prevSelected.some(
        (item) => item.indexName === indexName,
      );

      if (isSelected) {
        // Prevent deselecting if only one item is selected
        if (prevSelected.length === 1) {
          toast.error("At least one index must be selected");
          return prevSelected;
        }
        newSelection = prevSelected.filter(
          (item) => item.indexName !== indexName,
        );
      } else {
        if (prevSelected.length >= 10) {
          toast.warning("Maximum 10 indexes can be selected");
          return prevSelected;
        }
        newSelection = [...prevSelected, { indexName, _id }];
      }

      shouldFetchRef.current = true;
      return newSelection;
    });
  }, []);

  const filteredVesselIndexes = useMemo(() => {
    // UPDATED LOGIC: Removed val.isOwner so ALL indexes show in the list
    return vesselIndexesDetails.filter((val) =>
      val.indexName?.toLowerCase().includes(searchTerm?.toLowerCase()),
    );
  }, [searchTerm, vesselIndexesDetails]);

  const handleDateRangeChange = useCallback(async (newDateRange) => {
    const { startDate, endDate } = newDateRange;
    const formattedStartDate = startDate?.toISOString ? startDate.toISOString() : (startDate ? new Date(startDate).toISOString() : "");
    const formattedEndDate = endDate?.toISOString ? endDate.toISOString() : (endDate ? new Date(endDate).toISOString() : "");
    const newRange = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    };
    setDateRange(newRange);

    if (selectedOptionsRef.current.length) {
      const filter = {
        selectedOptions: selectedOptionsRef.current.map((o) => o.indexName),
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      };
      try {
        setLoader(true);
        const res = await actions.getVesselIndexes(filter);
        setVesselIndexesData(res?.data ?? []);
        setPrevFilters(filter);
      } catch (error) {
        toast.error("Failed to fetch vessel indexes");
        console.error(error);
      } finally {
        setLoader(false);
      }
    }
  }, [setLoader]);

  const handleResetSelectedOptions = () => {
    const ownerIndexes = vesselIndexesDetails
      .filter((val) => val.isOwner)
      .slice(0, 4);
    const defaultSelection =
      ownerIndexes.length > 0 ? ownerIndexes : vesselIndexesDetails.slice(0, 4);

    setSelectedOptions(defaultSelection);
    selectedOptionsRef.current = defaultSelection;
    shouldFetchRef.current = true;
    fetchVesselIndexes(true);
  };

  const onSetOpenMapForSelectedIndexes = () => {
    let ids = "";
    if (selectedOptions?.length > 0) {
      ids = selectedOptions?.map((item) => item._id).join(",");
    }

    if (ids) {
      window.open(`/vesselIndexMap/${ids}?myIndex=true`);
    } else {
      toast.warning("Please select an index to view route");
    }
  };

  const addNewIndex = async (payload) => {
    try {
      setSubLoader(true);
      const IsIndexExist = vesselIndexesDetails.find(
        (index) =>
          index.indexName?.toLowerCase() === payload.indexName?.toLowerCase(),
      );
      if (IsIndexExist) {
        toastError("Index already exists");
        setSubLoader(false);
        return;
      }
      await actions.addIndexDetails(payload);
      await fetchVesselIndexList();
      toastSuccess("Index added successfully");
      setIsmodelOpen(false);
      setFormData(initialFormState);
      setIndexValue([{ indexValue: 0, Date: "" }]);
      setSubLoader(false);
    } catch (error) {
      setSubLoader(false);
      toastError(error?.data?.message || "Error adding index");
      console.error(error);
    }
  };

  const updateIndex = async () => {
    try {
      setSubLoader(true);
      await actions.updateIndexDetails(formData._id, formData);
      await fetchVesselIndexList();
      toastSuccess("Index updated successfully");
      setIndexEditModelOpen(false);
      setSubLoader(false);
      setFormData(initialFormState);
    } catch (error) {
      setSubLoader(false);
      toastError(error?.data?.message || "Error updating index");
      console.error(error);
    }
  };

  const addIndexData = async (groupedPayload) => {
    try {
      let payload;
      if (!indexValue || indexValue.length === 0) {
        toastError("No index data to submit");
        return;
      }

      if (isCsvUploaded) {
        payload = groupedPayload;
      } else {
        payload = {
          indexName: selectedIndex,
          indexValues: indexValue.map((val) => ({
            Value: val.indexValue,
            Date: val.Date,
          })),
        };
      }

      await actions.addIndexData(payload);
      await fetchVesselIndexes(true);
      toastSuccess("Index data added successfully");
      setIndexValue([]);
      setSelectedIndex("");
      setIsInsertIndex(false);
      setIsCsvUploaded(false);
    } catch (error) {
      toastError(error?.data?.message || "Error adding data");
      console.error(error);
    }
  };

  return (
    <Grid container className="max-w-[100%] overflow-hidden">
      <Grid item xs={12} className="flex h-full">
        <Box className="fixed top-[50px] h-[calc(100vh-54px)] z-30">
          <div
            className={`relative -left-6 transition-all duration-300 ${hideSideBar ? "w-0 overflow-visible border-0" : "w-[280px]"
              } bg-white rounded-none border border-gray-300 shadow-sm h-full flex flex-col`}
          >
            {/* Toggle Button */}
            <button
              onClick={() => setHideSidebar((prev) => !prev)}
              className="absolute -right-6 top-4 w-6 h-9 bg-white border border-l-0 border-gray-300 text-[#313C4C] flex items-center justify-center transition hover:bg-gray-100 z-20 cursor-pointer shadow-sm"
              title={hideSideBar ? "Expand sidebar" : "Collapse sidebar"}
            >
              {hideSideBar ? <FaArrowRight size={13} /> : <FaArrowLeft size={13} />}
            </button>

            {/* Sidebar Content */}
            <div
              className={`p-2.5 flex flex-col h-full ${hideSideBar ? "hidden" : "block"}`}
            >
              {/* --- Sticky Header Section --- */}
              <div className="z-10 bg-white/95 backdrop-blur-sm px-2.5 pt-2.5 pb-2 border-b border-gray-200">
                <div className="flex justify-between items-center mb-2">
                  <h2 className="font-medium text-gray-800 text-[11px]">
                    Vessel Indexes
                    <span
                      className={`ml-1.5 text-[10px] py-0.5 px-1.5 rounded-none border ${selectedOptions?.length >= 10
                        ? "bg-rose-50 text-rose-600 border-rose-200 font-medium"
                        : "bg-[#e6f7ff] text-[#313C4C] border-[#313C4C]/20 font-medium"
                        }`}
                    >
                      {selectedOptions?.length}/10
                    </span>
                  </h2>

                  {/* Reset Button */}
                  <button
                    className="text-[11px] font-medium text-gray-800 hover:text-[#313C4C] transition-colors"
                    onClick={() => handleResetSelectedOptions()}
                  >
                    Reset
                  </button>
                </div>

                {/* Search Bar */}
                <div className="relative mb-2 group">
                  <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                    <FaMagnifyingGlass className="w-3 h-3 text-gray-500 group-focus-within:text-[#313C4C] transition-colors" />
                  </div>
                  <input
                    type="text"
                    className="w-full pl-8 pr-7 py-1 h-7 bg-gray-50 border border-gray-300 rounded-none text-[11px] font-medium focus:outline-none focus:ring-1 focus:ring-[#313C4C]/20 focus:border-[#313C4C] transition-all placeholder-gray-500 placeholder:font-medium"
                    placeholder="Search indexes..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-500 hover:text-gray-900 rounded-none hover:bg-gray-200 transition"
                      onClick={() => setSearchTerm("")}
                    >
                      <Close fontSize="small" style={{ fontSize: 13 }} />
                    </button>
                  )}
                </div>

                {/* Add Button & Limit Warning */}
                <div className="flex flex-col gap-1.5">
                  {selectedOptions?.length >= 10 && (
                    <div className="text-rose-600 bg-rose-50 border border-rose-100 px-2 py-1 rounded-none text-[10px] text-center font-medium">
                      Selection limit reached
                    </div>
                  )}

                  <Tooltip text="Create your own index" position="bottom">
                    <button
                      className="w-full h-7 flex items-center justify-center gap-1.5 text-[11px] font-medium rounded-none bg-[#313C4C] hover:bg-[#0a2a47] text-white shadow-sm transition-colors cursor-pointer"
                      onClick={() => {
                        setFormData(initialFormState);
                        setIsmodelOpen(true);
                      }}
                    >
                      <FaPlus size={11} className="shrink-0 relative top-[0.5px]" />
                      <span className="leading-none">Create New Index</span>
                    </button>
                  </Tooltip>
                </div>
              </div>

              {/* --- Scrollable List Section --- */}
              <div
                className="flex-1 overflow-y-auto overflow-x-hidden pt-1 pb-1 pr-2.5 pl-1 space-y-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
                style={{ maxHeight: "calc(100vh - 250px)" }}
              >
                {vesselIndexesDetails?.length ? (
                  filteredVesselIndexes?.length > 0 ? (
                    filteredVesselIndexes.map((val, index) => {
                      const isSelected = selectedOptions?.some(
                        (option) => option.indexName === val?.indexName,
                      );

                      const isDisabled =
                        !isSelected && selectedOptions?.length >= 10;

                      const portsDisplay =
                        Array.isArray(val?.port) && val.port.length > 0
                          ? val.port
                            .filter((p) => p?.name && p?.name !== "NA")
                            .map((p) =>
                              p?.country ? `${p.name}, ${p.country}` : p.name,
                            )
                            .join(" • ")
                          : "";

                      const tooltipContent = (
                        <div className="flex flex-col space-y-2 p-2 bg-white text-[11px] font-medium rounded-none">
                          <div>
                            <p className="text-[11px] font-medium text-[#313C4C]">
                              {val?.fullIndexName || val?.indexName || "—"}
                            </p>
                            <p className="text-[11px] text-gray-500 font-medium">
                              {val?.indexType || "—"}
                            </p>
                          </div>

                          <div className="flex flex-col space-y-0.5">
                            <p className="text-[11px] font-medium text-gray-800">
                              Description
                            </p>
                            <div className="max-h-[80px] overflow-y-auto scroll-thin bg-gray-50 p-1.5 rounded-none border border-gray-200">
                              <p className="text-[11px] text-gray-700 font-medium whitespace-pre-wrap">
                                {val?.briefDescription ||
                                  "No brief description"}
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-col space-y-0.5">
                            <p className="text-[11px] font-medium text-gray-800">
                              Details
                            </p>
                            <div className="max-h-[80px] overflow-y-auto scroll-thin bg-gray-50 p-1.5 rounded-none border border-gray-200">
                              <p className="text-[11px] text-gray-700 font-medium whitespace-pre-wrap">
                                {val?.indexDetails || "No index details"}
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-col space-y-0.5">
                            <p className="text-[11px] font-medium text-gray-800">
                              Vessel Details
                            </p>
                            <div className="max-h-[80px] overflow-y-auto scroll-thin bg-gray-50 p-1.5 rounded-none border border-gray-200">
                              <p className="text-[11px] text-gray-700 font-medium whitespace-pre-wrap">
                                {val?.vesselDetails || "No vessel details"}
                              </p>
                            </div>
                          </div>

                          {!!portsDisplay && (
                            <div className="flex flex-col space-y-0.5">
                              <p className="text-[11px] font-medium text-gray-800">
                                Ports
                              </p>

                              <div className="flex flex-wrap gap-1">
                                {portsDisplay.split(" • ").map((p, i) => (
                                  <span
                                    key={i}
                                    className="text-[11px] font-medium px-1.5 py-0.5 bg-[#e6f7ff] text-[#313C4C] rounded-none border border-[#313C4C]/20"
                                  >
                                    {p}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );

                      return (
                        <CustomTooltip
                          key={val._id || index}
                          content={tooltipContent}
                        >
                          <div
                            onClick={() => {
                              if (!isDisabled) {
                                toggleVesselIndexSelection({
                                  indexName: val?.indexName,
                                  _id: val?._id,
                                });
                              }
                            }}
                            className={`relative rounded-none border px-2 py-1.5 transition 
                                w-full min-w-0 text-[11px] font-medium
                                ${isSelected
                                ? "border-[#313C4C]/40 bg-[#e6f7ff]"
                                : isDisabled
                                  ? "border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed"
                                  : "border-gray-300 hover:border-[#313C4C]/40 cursor-pointer"
                              }`}
                          >
                            <div className="flex justify-between items-start w-full">
                              <div className="flex-1 min-w-0 pr-1">
                                <p
                                  className={`text-[11px] font-medium leading-tight truncate ${isSelected
                                    ? "text-[#313C4C]"
                                    : "text-gray-800"
                                    }`}
                                >
                                  {val?.fullIndexName || val?.indexName}
                                </p>

                                <p className="text-[10px] font-medium text-gray-500 mt-0">
                                  {val?.indexType || "Unknown Type"}
                                </p>
                              </div>

                              <div className="flex items-center gap-1">
                                {(isSuperAdmin || val?.isOwner) && (
                                  <Tooltip text="Edit Index" position="left">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setIndexEditModelOpen(true);
                                        setFormData(val);
                                      }}
                                      className={`p-0.5 text-gray-500 hover:text-[#313C4C] transition ${isDisabled ? "text-gray-300" : ""}`}
                                      disabled={isDisabled}
                                    >
                                      <Edit style={{ fontSize: 13 }} />
                                    </button>
                                  </Tooltip>
                                )}

                                <Tooltip text="View Details" position="left">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setisOpenVesselIndexInfoModel(true);
                                      setVesselIndexInfo(val);
                                    }}
                                    className="p-0.5 text-gray-500 hover:text-[#313C4C] transition"
                                  >
                                    <InfoOutlined style={{ fontSize: 13 }} />
                                  </button>
                                </Tooltip>
                              </div>
                            </div>

                            {val?.port?.length > 0 &&
                              val?.port?.some((p) => p?.name && p?.name !== "NA") && <Divider className="my-0.5" />}

                            {val?.port?.length > 0 && (
                              <p className="mt-0 text-[10px] font-medium text-gray-600 leading-tight truncate">
                                {val?.port
                                  ?.filter((p) => p?.name && p?.name !== "NA")
                                  .map((p) => (p?.country ? `${p?.name}, ${p?.country}` : p?.name))
                                  .join(" • ")}
                              </p>
                            )}
                          </div>
                        </CustomTooltip>
                      );
                    })
                  ) : (
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                        <Map style={{ fontSize: 24, color: "#9ca3af" }} />
                      </div>

                      <p className="text-sm font-medium text-gray-900">
                        No indexes found
                      </p>

                      <p className="text-xs text-gray-900 mt-1">
                        Try adjusting your search term
                      </p>
                    </div>
                  )
                ) : hasFetched ? (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <p className="text-sm text-gray-900">
                      No Vessel Indexes available.
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </Box>
        <Box style={{ width: hideSideBar ? '100%' : 'calc(100% - 295px)', marginLeft: hideSideBar ? '0' : '295px' }} className="transition-all duration-300 pl-0 pr-0 pt-0">
          <div className="max-w-[100%] mx-auto">
            <AvgMovingChart
              data={vesselIndexesData}
              selectedOptions={selectedOptions?.map(
                (option) => option.indexName,
              )}
              handleSubmit={fetchVesselIndexes}
              onDateRangeChange={handleDateRangeChange}
              date={dateRange}
              setDate={setDateRange}
              onViewRoute={onSetOpenMapForSelectedIndexes}
              onInsertIndexData={() => setIsInsertIndex(true)}
            />
          </div>
        </Box>
      </Grid>

      {/* Create / Edit Index Modal */}
      <CreateIndexModel
        open={isModelOpen || indexEditModelOpen}
        title={`${indexEditModelOpen ? "Update" : "Create"} Index`}
        buttonTitle={`${indexEditModelOpen ? "Update" : "Create"}`}
        handleClose={() => {
          indexEditModelOpen
            ? setIndexEditModelOpen(false)
            : setIsmodelOpen(false);
        }}
        portOptions={portOptions}
        selectedPort={selectedPort}
        onChangePort={onChangePort}
        delayPortSearch={delayPortSearch}
        portLoading={portLoading}
        onSubmit={indexEditModelOpen ? updateIndex : addNewIndex}
        indexValue={indexValue}
        setIndexValue={setIndexValue}
        formData={formData}
        setFormData={setFormData}
        subLoader={subLoader}
        isEdit={indexEditModelOpen}
      />
      <InsertIndexModel
        open={isInsertIndex}
        title={"Insert Index Data"}
        buttonTitle={"Add"}
        handleClose={() => {
          setIsInsertIndex(false);
        }}
        onSubmit={addIndexData}
        indexValue={indexValue}
        setIndexValue={setIndexValue}
        subLoader={subLoader}
        isCsvUploaded={isCsvUploaded}
        setIsCsvUploaded={setIsCsvUploaded}
        vesselIndexesData={vesselIndexesData}
        indexList={vesselIndexesDetails}
        setSelectedIndex={setSelectedIndex}
        selectedIndex={selectedIndex}
      />

      {/* Vessel Index Info Modal (Detail View) */}
      <Dialog
        className="superadmin-dialog"
        open={isOpenVesselIndexInfoModel}
        onClose={() => setisOpenVesselIndexInfoModel(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          className: "!rounded-none border border-slate-200 shadow-xl overflow-hidden",
        }}
      >
        <DialogTitle className="!p-0">
          <div className="flex justify-between items-center px-3 py-1.5 bg-[#f8fafc] border-b border-slate-200">
            <span className="text-[14px] font-medium text-slate-900">
              {vesselIndexInfo?.indexName} Details
            </span>
            <button
              onClick={() => setisOpenVesselIndexInfoModel(false)}
              className="p-0.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center"
              title="Close"
              aria-label="Close index details"
            >
              <Close style={{ fontSize: 16 }} />
            </button>
          </div>
        </DialogTitle>
        <DialogContent dividers className="!px-3 !py-3 bg-white scroll-thin">
          <div className="space-y-2">
            <div>
              <p className="text-[11px] font-semibold text-[#313C4C] mb-0.5">
                Brief Description
              </p>
              <p className="text-[11px] text-slate-600 font-medium leading-relaxed whitespace-pre-wrap">
                {vesselIndexInfo?.briefDescription || "N/A"}
              </p>
            </div>
            <div className="border-t border-slate-100" />
            <div>
              <p className="text-[11px] font-semibold text-[#313C4C] mb-0.5">
                Index Details
              </p>
              <p className="text-[11px] text-slate-600 font-medium leading-relaxed whitespace-pre-wrap">
                {vesselIndexInfo?.indexDetails || "N/A"}
              </p>
            </div>
            <div className="border-t border-slate-100" />
            <div>
              <p className="text-[11px] font-semibold text-[#313C4C] mb-0.5">
                Vessel Details
              </p>
              <p className="text-[11px] text-slate-600 font-medium leading-relaxed whitespace-pre-wrap">
                {vesselIndexInfo?.vesselDetails || "N/A"}
              </p>
            </div>
            {vesselIndexInfo?.port?.length > 0 && (
              <>
                <div className="border-t border-slate-100" />
                <div>
                  <p className="text-[11px] font-semibold text-[#313C4C] mb-1">
                    Ports
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {vesselIndexInfo.port.map((p, i) => (
                      <span
                        key={i}
                        className="text-[11px] font-medium bg-sky-50 text-[#313C4C] border border-[#313C4C]/20 px-1.5 py-0.5 rounded-none"
                      >
                        {p.name}
                        {p.country ? `, ${p.country}` : ""}
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
        <DialogActions className="!px-3 !py-1.5 bg-[#f8fafc] border-t border-slate-200 flex justify-end gap-2">
          <button
            onClick={() => {
              window.open(
                `/vesselIndexMap/${vesselIndexInfo?._id}?myIndex=true`,
              );
              setisOpenVesselIndexInfoModel(false);
            }}
            className="h-7 px-2.5 text-[11px] font-medium border border-slate-300 text-[#313C4C] bg-white hover:bg-slate-50 rounded-none transition-colors cursor-pointer flex items-center"
          >
            View On Map
          </button>
          <button
            onClick={() => setisOpenVesselIndexInfoModel(false)}
            className="h-7 px-3 text-[11px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-200/60 rounded-none border-none bg-transparent transition-colors cursor-pointer flex items-center"
          >
            Close
          </button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default MyMarketIndex;
